import { connect, getIn } from 'formik';
import { useState, useMemo, useRef } from 'react';
import Autosuggest from 'react-autosuggest';

import { Box } from '@hivebrite/design-system/Box';
import { toast } from '@hivebrite/design-system/Toast';

import { useDonationContext } from 'hb-react/frontoffice/contexts/DonationContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { dynamicDonationResourcePath } from 'hb-react/shared/apps/Donations/utils/whiteLabel';
import { Hidden } from 'hb-react/shared/components/formik/components/inputs';
import { axiosInstance as axios, isCancel } from 'hb-react/shared/utils/axios';

import CompanyDetails from './CompanyDetails';
import AutosuggestStyle from './style';

const HEP = ({ formik }) => {
  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const { currentUser } = useDefaultAppContexts();
  const { whitelabelURL } = useDonationContext();
  const whitelabelURLEnabled = useMemo(
    () => (currentUser ? false : whitelabelURL),
    [currentUser, whitelabelURL],
  );

  const abortController = useRef();

  // LOGIC

  const getSuggestionValue = ({ name }) => name;

  const onSuggestionSelected = (_event, { suggestion }) => {
    formik.setFieldValue(
      'donation_order[hep_company_id]',
      suggestion.company_id,
    );
  };

  // HANDLERS

  const onChange = (_event, { newValue = '' }) => {
    formik.setFieldValue('donation_order[hep_company_id]', '');
    setValue(newValue);
  };

  const onSuggestionsFetchRequested = ({ value = '' }) => {
    setSuggestions([]);
    if (value.length > 2) {
      abortController.current?.abort();

      abortController.current = new AbortController();

      const path = dynamicDonationResourcePath(
        Routes.donations_hep_search_path({ query: value }),
        {
          whitelabelURL: whitelabelURLEnabled,
        },
      );

      axios
        .get(path, { signal: abortController.current.signal })
        .then((response) => {
          setSuggestions(response?.data?.items);
        })
        .catch((error) => {
          if (!isCancel(error)) {
            toast.error(__('Sorry, an error occurred'));
          }
        });
    }
  };

  // RENDERERS

  const renderSuggestion = (suggestion) => <div>{suggestion?.name}</div>;

  const renderInputComponent = (inputProps) => {
    const companyId = getIn(formik.values, 'donation_order[hep_company_id]');

    return (
      <div className="input-with-icon">
        <input {...inputProps} />
        {companyId ? (
          <i
            title={__('The company is selected')}
            className="icon-check color-green absolute east push-half--right f-size--1-4"
          />
        ) : (
          <i className="icon-magnifying-glass absolute east push-half--right f-size--1-3" />
        )}
      </div>
    );
  };

  const companyId = getIn(formik.values, 'donation_order[hep_company_id]');

  return (
    <div>
      <Hidden name="donation_order[hep_company_id]" />
      <Box as="h4" mb={5} font="h4-semibold">
        {__('Matching gift information')}
      </Box>
      <Box mb={3} font="body-regular-dove">
        {__(
          'To make your gift go even further, check to see if your employer has a matching gift program.',
        )}
      </Box>
      <div className="flex flex-center flex-wrap">
        <div className="one-half palm--one-whole push--right palm--flush--right palm--push--bottom z-index--1">
          <Autosuggest
            theme={AutosuggestStyle}
            suggestions={suggestions}
            getSuggestionValue={getSuggestionValue}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={() => {}}
            onSuggestionSelected={onSuggestionSelected}
            renderSuggestion={renderSuggestion}
            renderInputComponent={renderInputComponent}
            inputProps={{
              placeholder: __('Search for your company'),
              value,
              onChange,
            }}
          />
        </div>
        {companyId && <CompanyDetails companyId={companyId} />}
      </div>
    </div>
  );
};

export default connect(HEP);
